import { AssignedConfigsDto, CompaniesApi, CompanyBasicInfoDto, Configuration, CreateSenderIdentityDto, PropertyGroupDetailDto, PropertyGroupsApi, SearchSenderIdentityDto } from '@inosio/payments-sdk';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { IpaUserExtended, selectCurrentUser } from '../auth/authSlice';
import { AxiosResponse } from 'axios';
import {
  getAllPropertyGroupsConfigs,
  getCompany,
  getPropertyGroupDetails,
  getPropertyGroups,
  getUpdatePropertyGroupConfigs,
  handleGetCompanyFailed,
  handleGetCompanySuccess,
  setAllPropertyGroupsConfigs,
  setAllPropertyGroupConfigsError,
  setPropertyGroupDetails,
  setPropertyGroupDetailsError,
  setPropertyGroups,
  setPropertyGroupsError,
  setUpdatePropertyGroupConfigs,
  setUpdatePropertyGroupConfigsError,
  getSenderIdentities,
  createSenderIdentity,
  updateSenderIdentity,
  deleteSenderIdentity,
  setCreateSenderIdentity,
  setCreateSenderIdentityFailed,
  setDeleteSenderIdentity,
  setDeleteSenderIdentityFailed,
  setSenderIdentities,
  setSenderIdentitiesFetchError,
} from './propertyGroupsSlice';
const readOnlyConfigKeys = ['MaxAllowedPayment'];

const { REACT_APP_IPA_BASE_PATH } = process.env;
const apiConfig = new Configuration({ basePath: REACT_APP_IPA_BASE_PATH });

export function requestGetPropertyGroupDetails(access_token: string, propertyGroupUuid: string, productTypeId?: number[]) {
  apiConfig.accessToken = access_token;
  const ipaPgApi = new PropertyGroupsApi(apiConfig);

  return ipaPgApi.getPropertyGroup(
    propertyGroupUuid,
    productTypeId?.map((p) => p.toString()),
  );
}

export function requestGetAllPropGroupsConfigsSettings(access_token: string) {
  apiConfig.accessToken = access_token;
  const pgApi = new PropertyGroupsApi(apiConfig);
  return pgApi.getAllPropertyGroupsConfigs();
}

export function requestGetPropGroups(access_token: string) {
  apiConfig.accessToken = access_token;
  const pgApi = new PropertyGroupsApi(apiConfig);
  return pgApi.getAllPropertyGroups();
}

export function requestUpdatePropertyGroupConfigSettings(access_token: string, propertyGroupUuid: string, productTypeId: number, setToDefault: boolean, pmConfigObj: AssignedConfigsDto) {
  apiConfig.accessToken = access_token;
  const pgApi = new PropertyGroupsApi(apiConfig);
  return pgApi.updatePropertyGroupConfigs(propertyGroupUuid, productTypeId, setToDefault, pmConfigObj);
}

export function requestGetCompany(access_token: string, companyUuid: string) {
  apiConfig.accessToken = access_token;
  const ipaCompaniesAPI = new CompaniesApi(apiConfig);
  return ipaCompaniesAPI.getCompanyDetail(companyUuid);
}

export function requestFetchSenderIdentities(accessToken: string,propertyGroupUuid: string, searchSenderIdentityDto: SearchSenderIdentityDto) {
  apiConfig.accessToken = accessToken;
  const pgApi = new PropertyGroupsApi(apiConfig);
  return pgApi.getPropertyGroupSenderIdentities(propertyGroupUuid, searchSenderIdentityDto);
}

export function requestCreateSenderIdentity(accessToken: string,propertyGroupUuid: string, createSenderIdentityDto: CreateSenderIdentityDto) {
  apiConfig.accessToken = accessToken;
  const pgApi = new PropertyGroupsApi(apiConfig);
  return pgApi.createPropertyGroupSenderIdentity(propertyGroupUuid, createSenderIdentityDto);
}

export function requestDeleteSenderIdentity(accessToken: string,propertyGroupUuid: string, senderIdentityUuid: string) {
  apiConfig.accessToken = accessToken;
  const pgApi = new PropertyGroupsApi(apiConfig);
  return pgApi.deletePropertyGroupSenderIdentity(propertyGroupUuid, senderIdentityUuid);
}



export function* handleGetPropertyGroupDetails(action: { type: typeof getPropertyGroupDetails; payload: { propertyGroupUuid: string; productTypeId?: number[] } }) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<PropertyGroupDetailDto> = yield call(requestGetPropertyGroupDetails, currentUser?.accessToken, action.payload.propertyGroupUuid, action.payload.productTypeId);
    const { data } = response;
    yield put(setPropertyGroupDetails(data));
  } catch (error: any) {
    yield put(
      setPropertyGroupDetailsError({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleGetAllPropGroupsConfigsSettings(action: { type: typeof getAllPropertyGroupsConfigs; payload: string }) {
  try {
    //TODO: //no need to send additional requests if one is still in flight:
    // const { isFetchingAllPropertyGroupConfigs } = yield select(selectPropertyGroupDetailsState);
    // const fred: PropertyGroupState  = yield select(selectPropertyGroupDetailsState);

    // if (fred.isFetchingAllPropertyGroupConfigs) {
    //   return;
    // }
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<AssignedConfigsDto> = yield call(requestGetAllPropGroupsConfigsSettings, currentUser.accessToken);
    const { data } = response;
    yield put(setAllPropertyGroupsConfigs(data));
  } catch (error: any) {
    yield put(setAllPropertyGroupConfigsError({ message: error?.response?.data?.message }));
  }
}

export function* handleGetPropGroups(action: { type: typeof getPropertyGroups }) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<PropertyGroupDetailDto[]> = yield call(requestGetPropGroups, currentUser.accessToken);

    const { data } = response;

    yield put(setPropertyGroups(data));
  } catch (error: any) {
    yield put(setPropertyGroupsError({ message: error?.response?.data?.message }));
  }
}

export function* handleUpdatePropertyGroupConfigSettings(action: { type: typeof getUpdatePropertyGroupConfigs; payload: any }) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);

    const { propertyGroupUuid, productTypeId, setToDefault, updatedPmConfig } = action.payload;
    readOnlyConfigKeys.forEach((key) => {
      if (updatedPmConfig.hasOwnProperty(key)) delete updatedPmConfig[key];
    });

    if (Object.keys(updatedPmConfig).length === 0 && !setToDefault) {
      //*what scenario is this?
      yield delay(500);
      yield put(setUpdatePropertyGroupConfigs(updatedPmConfig));
    } else {
      const response: AxiosResponse<AssignedConfigsDto> = yield call(requestUpdatePropertyGroupConfigSettings, currentUser.accessToken, propertyGroupUuid, productTypeId, !!setToDefault, updatedPmConfig);
      if (response.status === 200) {
        const { data } = response;
        yield put(setUpdatePropertyGroupConfigs(data));
      }
    }
  } catch (error: any) {
    yield put(setUpdatePropertyGroupConfigsError({ message: error?.response?.data?.message }));
  }
}

export function* handleGetCompany(action: any) {
  try {
    const currentUser:IpaUserExtended= yield select(selectCurrentUser);
    const response: AxiosResponse<CompanyBasicInfoDto> = yield call(requestGetCompany, currentUser?.accessToken, action.payload.companyUuid);
    const { data } = response;
    yield put(handleGetCompanySuccess(data));
  } catch (error: any) {
    yield put(
      handleGetCompanyFailed({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}




export function* handleFetchSenderIdentities(action: { type: typeof getSenderIdentities; payload: {
  propertyGroupUuid: string,
  searchSenderIdentityDto: SearchSenderIdentityDto
} }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestFetchSenderIdentities, currentUser.accessToken, action.payload.propertyGroupUuid, action.payload.searchSenderIdentityDto);
    const { data } = response;

    yield put(setSenderIdentities(data));
  } catch (error: any) {
    yield put(
      setSenderIdentitiesFetchError({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleCreateSenderIdentity(action: { type: typeof createSenderIdentity; payload: {
  propertyGroupUuid: string,
  createSenderIdentityDto: CreateSenderIdentityDto
} }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
    const response: AxiosResponse<any> = yield call(requestCreateSenderIdentity, currentUser.accessToken, action.payload.propertyGroupUuid, action.payload.createSenderIdentityDto);
    const { data } = response;
    yield put(setCreateSenderIdentity(data));
  } catch (error: any) {
    yield put(
      setCreateSenderIdentityFailed({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* handleUpdateSenderIdentity(action: { type: typeof updateSenderIdentity; payload: { propertyGroupUuid: string;senderIdentityUuuid: string, updateSenderIdentityDto: any } }) {
 
}

export function* handleDeleteSenderIdentity(action: { type: typeof deleteSenderIdentity; payload: { propertyGroupUuid: string, senderIdentityUuid: string } }) {
  try {
    const currentUser: IpaUserExtended = yield select(selectCurrentUser);
     yield call(requestDeleteSenderIdentity, currentUser.accessToken, action.payload.propertyGroupUuid, action.payload.senderIdentityUuid);
    yield put(setDeleteSenderIdentity(action.payload.senderIdentityUuid));
  } catch (error: any) {
    yield put(
      setDeleteSenderIdentityFailed({
        message: error?.response?.data?.message,
        statusCode: error?.response?.data?.statusCode,
      }),
    );
  }
}

export function* propertyGroupsSaga() {
  yield takeLatest(getPropertyGroupDetails.type, handleGetPropertyGroupDetails);
  yield takeLatest(getAllPropertyGroupsConfigs.type, handleGetAllPropGroupsConfigsSettings);
  yield takeLatest(getUpdatePropertyGroupConfigs.type, handleUpdatePropertyGroupConfigSettings);
  yield takeLatest(getCompany.type, handleGetCompany);
  yield takeLatest(getPropertyGroups.type, handleGetPropGroups);

  yield takeLatest(getSenderIdentities.type, handleFetchSenderIdentities);
  yield takeLatest(createSenderIdentity.type, handleCreateSenderIdentity);
  yield takeLatest(updateSenderIdentity.type, handleUpdateSenderIdentity);
  yield takeLatest(deleteSenderIdentity.type, handleDeleteSenderIdentity);
}
